/**
 * The canonical shape of every "you have unsaved changes" prompt in Contact.
 *
 * These constants used to live in `apps/contact-admin/src/composables/useUnsavedChangesGuard.ts`,
 * which shared/frontend cannot import — so shared components that show the same prompt (notably
 * `media/ImageEditor.vue`, which contact-admin AND contact-async both render) grew their own
 * hand-rolled copy with a red destructive primary. LSP-19492: one definition, here, re-exported
 * from the contact-admin composable so every existing import keeps working.
 *
 * Nothing in this module imports Vue or touches a host application — it is plain data plus one
 * function that takes the host's already-resolved `$confirm` dialog.
 */

/**
 * Shared dialog options for the three-way unsaved-changes prompt
 * (Save / Don't save / Cancel). Re-used across every route guard that shows
 * this dialog to keep them in sync.
 *
 * Action-oriented labels (LSP-15724) — the buttons describe the outcome so
 * users don't have to re-read the message to know what each button does.
 */
export const UNSAVED_CHANGES_DIALOG_OPTIONS = {
	width: "500px",
	persistent: true,
	customButton: true,
	customText: "Don't save",
	customButtonVariant: "outlined",
	customButtonColor: "primary",
	customButtonBeforeAgree: true,
	cancelResolveValue: null
} as const;

/**
 * Shared button copy for the three-way unsaved-changes prompt. The primary
 * (agree) action saves, the custom button discards, and the cancel button
 * keeps the user on the page (LSP-15724).
 */
export const UNSAVED_CHANGES_DIALOG_TEXT = {
	title: "You have unsaved changes",
	message: "Would you like to save your changes before leaving?",
	agreeText: "Save",
	cancelText: "Cancel"
} as const;

/**
 * Options for the two-way discard prompt shown when the form is dirty but can't
 * be saved. Deliberately the same footer shape as the three-way prompt above
 * (LSP-16869): the cancel button is a borderless text button on the left, and
 * the leave-without-saving action sits on the right as an outlined button. There
 * is no Save here — that's the whole point of this prompt — so the agree button
 * is removed and the discard action rides the custom-button slot, which is what
 * gives it the same treatment "Don't save" gets in the three-way prompt.
 *
 * `customResolveValue: true` keeps the discard action resolving `true` now that
 * it is the custom button rather than the agree button, so every caller's
 * "truthy means discard" reading is unchanged. `confirmDiscardChanges` supplies
 * `customText` per call from the caller's own copy.
 */
export const INVALID_CHANGES_DIALOG_OPTIONS = {
	width: "500px",
	persistent: true,
	customButton: true,
	customButtonVariant: "outlined",
	customButtonColor: "primary",
	removeAgreeButton: true,
	customResolveValue: true,
	cancelResolveValue: null
} as const;

/**
 * Copy for the discard prompt. Matches the three-way prompt's voice (LSP-16869):
 * the same title, a message that names the blocker and then asks the same
 * "Would you like to…?" question, and the same button words — so a user who
 * leaves a dirty page sees one consistent prompt whether or not saving is
 * possible. `agreeText` labels the discard action wherever it renders (here, the
 * custom button); the field name is shared with the three-way prompt's shape.
 */
export const INVALID_CHANGES_DIALOG_TEXT = {
	title: "You have unsaved changes",
	message: "Some fields still need fixing before we can save. Would you like to leave without saving?",
	agreeText: "Don't save",
	cancelText: "Cancel"
} as const;

export type DiscardDialogText = {
	title: string;
	message: string;
	agreeText: string;
	cancelText: string;
};

/** The host application's confirm dialog, as reached through `$root.$confirm`. */
export type ConfirmDialogApi = {
	open: (
		title: string,
		message: string,
		requireDeleteConfirmation: boolean,
		agreeText: string,
		cancelText: string,
		isAngry: boolean,
		removeCancelButton: boolean,
		options?: Record<string, unknown>
	) => Promise<boolean | null>;
};

/**
 * Opens the canonical discard prompt on an already-resolved confirm dialog.
 *
 * Every caller routes through here rather than assembling the eight positional arguments itself:
 * argument six is `isAngry`, and passing `true` there is exactly what painted ImageEditor's
 * "Yes" button destructive red (LSP-19492). Resolves truthy when the user chose to discard and
 * `null` when they backed out, matching `INVALID_CHANGES_DIALOG_OPTIONS`.
 */
export function openDiscardChangesDialog(
	confirmDialog: ConfirmDialogApi,
	text: DiscardDialogText = INVALID_CHANGES_DIALOG_TEXT
): Promise<boolean | null> {
	return confirmDialog.open(
		text.title,
		text.message,
		false,
		text.agreeText,
		text.cancelText,
		false,
		false,
		// The discard action renders as the custom button (see INVALID_CHANGES_DIALOG_OPTIONS), so
		// the caller's own discard label has to travel as `customText` — the positional agreeText
		// above labels a button this prompt removes.
		{ ...INVALID_CHANGES_DIALOG_OPTIONS, customText: text.agreeText }
	);
}
